import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Modal, Input, Alert, EmptyState } from '../components/design-system';
import { Key, Plus, Trash2, Copy, CheckCircle, Clock } from 'lucide-react';

export const ApiKeysPage = () => {
  const { isAdmin } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);

  useEffect(() => { loadKeys(); }, []);

  const loadKeys = async () => {
    try {
      const res = await api.getApiKeys();
      setKeys(res.apiKeys || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    try { await api.deleteApiKey(id); loadKeys(); }
    catch (err) { alert(err.message); }
  };

  const handleToggle = async (id, isActive) => {
    try { await api.updateApiKey(id, { isActive: !isActive }); loadKeys(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
          <p className="text-sm text-gray-500">Manage access keys for the public news API</p>
        </div>
        {isAdmin && <Button icon={Plus} onClick={() => setShowCreate(true)}>Create Key</Button>}
      </div>

      {/* New key display */}
      {newKey && (
        <Alert type="warning" onClose={() => setNewKey(null)}>
          <div>
            <p className="font-medium mb-1">Save your API key now — it won't be shown again!</p>
            <div className="flex items-center gap-2 bg-yellow-100 rounded p-2 font-mono text-xs">
              <code className="flex-1 break-all">{newKey}</code>
              <button onClick={() => { navigator.clipboard.writeText(newKey); }} className="p-1 hover:bg-yellow-200 rounded">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>
      ) : keys.length === 0 ? (
        <EmptyState icon={Key} title="No API keys" description="Create API keys to allow external platforms to consume your generated news."
          action={isAdmin && <Button icon={Plus} onClick={() => setShowCreate(true)}>Create First Key</Button>} />
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <Card key={k.id} className="!p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{k.name}</span>
                    <Badge variant={k.is_active ? 'success' : 'default'} dot>{k.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{k.key_prefix}••••••••</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>Rate: {k.rate_limit}/{k.rate_window_minutes}min</span>
                    <span>{k.total_requests || 0} requests</span>
                    {k.last_used_at && <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                    {k.expires_at && <span className="text-orange-500">Expires {new Date(k.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleToggle(k.id, k.is_active)}>
                    {k.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="danger" size="sm" icon={Trash2} onClick={() => handleDelete(k.id)}>Revoke</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={(key) => { setNewKey(key); setShowCreate(false); loadKeys(); }}
        />
      )}
    </div>
  );
};

const CreateKeyModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', rateLimit: 1000, rateWindowMinutes: 60, expiresAt: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.createApiKey({
        name: form.name,
        rateLimit: parseInt(form.rateLimit),
        rateWindowMinutes: parseInt(form.rateWindowMinutes),
        expiresAt: form.expiresAt || undefined,
      });
      onCreated(res.apiKey.key);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Create API Key">
      {error && <Alert type="error" className="mb-4">{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Key Name" placeholder="e.g. Mobile App Production" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Rate Limit (requests)" type="number" min="1" value={form.rateLimit}
            onChange={(e) => setForm({ ...form, rateLimit: e.target.value })} />
          <Input label="Window (minutes)" type="number" min="1" value={form.rateWindowMinutes}
            onChange={(e) => setForm({ ...form, rateWindowMinutes: e.target.value })} />
        </div>
        <Input label="Expires At (optional)" type="date" value={form.expiresAt}
          onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading}>Create Key</Button>
        </div>
      </form>
    </Modal>
  );
};
